<template>
    <nav class="canvas-toolbar mb-5" aria-label="Canvas tools">
        <div class="toolbar-groups">
            <div v-for="(group, groupIndex) in toolGroups" :key="groupIndex" class="toolbar-group">
                <button
                    v-for="tool in group"
                    :key="tool.id"
                    class="tool"
                    type="button"
                    :aria-pressed="isActive(tool)"
                    :class="{ active: isActive(tool) }"
                    :title="tool.label"
                    @click="selectTool(tool)"
                >
                    <span class="tool__icon" v-html="tool.icon" aria-hidden="true"></span>
                    <span v-if="tool.dropdown" class="tool__caret" aria-hidden="true">▾</span>
                </button>
            </div>
        </div>
        <div class="toolbar-utility">
            <button
                v-for="tool in utilityTools"
                :key="tool.id"
                class="tool"
                type="button"
                :aria-pressed="activeUtility === tool.id"
                :class="{ active: activeUtility === tool.id }"
                :title="tool.label"
                @click="selectUtility(tool)"
            >
                <span class="tool__icon" v-html="tool.icon" aria-hidden="true"></span>
            </button>
        </div>
    </nav>
</template>

<script>
const icons = {
    arrow: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M3 3l10 4-4 2-2 4z" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    layout: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><rect x="2.5" y="2.5" width="11" height="11" rx="2" stroke-width="1.2"/><path d="M5.5 2.5v11M10.5 2.5v11M2.5 8h11" stroke-width="1.2" stroke-linecap="round"/></svg>',
    rectangle: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><rect x="3" y="3" width="10" height="10" rx="1.8" stroke-width="1.2"/></svg>',
    pen: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M3 13l1.8-4.7L11.3 1.8a1.4 1.4 0 012 2l-6.5 6.5L3 13z" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10.2" cy="5.8" r="0.8" fill="currentColor"/></svg>',
    text: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M3 4.2h10M5.6 4.2v8M10.4 4.2v8M3 12.6h10" stroke-width="1.2" stroke-linecap="round"/></svg>',
    ellipse: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><ellipse cx="8" cy="8" rx="4.8" ry="4.2" stroke-width="1.2"/></svg>',
    component: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><circle cx="5" cy="5" r="1.2"/><circle cx="11" cy="5" r="1.2"/><circle cx="5" cy="11" r="1.2"/><circle cx="11" cy="11" r="1.2"/><path d="M5 5h6M5 11h6M5 5v6M11 5v6" stroke-width="1.2" stroke-linecap="round"/></svg>',
    frame: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M5 1.8h6M5 14.2h6M1.8 5v6M14.2 5v6" stroke-width="1.2" stroke-linecap="round"/></svg>',
    code: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M5.2 5l-2.8 3 2.8 3M10.8 5l2.8 3-2.8 3" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
};

export default {
    name: 'ToolbarComp',
    data() {
        return {
            activeTool: 'rectangle',
            activeUtility: 'frame',
            toolGroups: [
                [
                    { id: 'arrow', label: 'Move Tool', icon: icons.arrow, dropdown: true }
                ],
                [
                    { id: 'layout', label: 'Layout Grid', icon: icons.layout, dropdown: true }
                ],
                [
                    { id: 'rectangle', label: 'Rectangle Tool', icon: icons.rectangle, dropdown: true },
                    { id: 'pen', label: 'Pen Tool', icon: icons.pen, dropdown: true }
                ],
                [
                    { id: 'text', label: 'Text Tool', icon: icons.text },
                    { id: 'ellipse', label: 'Ellipse Tool', icon: icons.ellipse }
                ],
                [
                    { id: 'component', label: 'Team Library', icon: icons.component }
                ]
            ],
            utilityTools: [
                { id: 'style', label: 'Style', icon: icons.pen },
                { id: 'frame', label: 'Frame Mode', icon: icons.frame },
                { id: 'code', label: 'Code Inspect', icon: icons.code }
            ]
        };
    },
    methods: {
        isActive(tool) {
            return this.activeTool === tool.id;
        },
        selectTool(tool) {
            this.activeTool = tool.id;
        },
        selectUtility(tool) {
            this.activeUtility = tool.id;
        }
    }
};
</script>

<style scoped>
.canvas-toolbar {
    position: absolute;
    left: 50%;
    bottom: 24px;
    display: inline-flex;
    align-items: center;
    padding: 8px;
    transform: translateX(-50%);
    border-radius: 16px;
    background: var(--titlebar);
    border: 1px solid rgba(255, 255, 255, 0.06);
    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.42);
    backdrop-filter: blur(12px);
    gap: 8px;
    z-index: 5;
}

.toolbar-groups {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-right: 6px;
}

.toolbar-group {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    border-radius: 12px;
    background: var(--panel);
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.toolbar-utility {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px;
    border-radius: 12px;
    background: var(--panel);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.tool {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: rgba(255, 255, 255, 0.82);
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.tool__icon svg {
    width: 16px;
    height: 16px;
}

.tool__caret {
    font-size: 10px;
    margin-left: 2px;
    color: rgba(255, 255, 255, 0.5);
}

.tool:hover,
.tool:focus {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.95);
    outline: none;
}

.tool.active {
    border-color: var(--accent);
    background: rgba(91, 140, 255, 0.16);
    color: var(--accent);
}

.toolbar-utility .tool {
    border-radius: 10px;
    padding: 0 8px;
}

.toolbar-utility .tool.active {
    background: rgba(91, 140, 255, 0.2);
    border-color: transparent;
    color: #5b8cff;
}

.toolbar-utility .tool:not(.active):hover {
    background: rgba(255, 255, 255, 0.07);
}

@media (max-width: 1024px) {
    .canvas-toolbar {
        bottom: 16px;
        padding: 6px;
        gap: 6px;
    }

    .toolbar-group {
        padding: 4px 6px;
    }

    .tool {
        padding: 0 8px;
    }
}
</style>
